import type { PageSetup } from './paper';

/** The three slots of one running header or footer row. */
export type HeaderFooterSlots = { left: string; center: string; right: string };

/** A pattern's running header and footer, drawn in the margin bands of every page. */
export type HeaderFooter = {
    header: HeaderFooterSlots;
    footer: HeaderFooterSlots;
    /** Hide both on the first page (a cover). */
    hideOnFirstPage: boolean;
};

export type SlotName = 'left' | 'center' | 'right';
export const SLOT_NAMES: readonly SlotName[] = ['left', 'center', 'right'];

export const HEADER_FOOTER_LIMITS = { slot: 200 } as const;

/** 10 pt in CSS px, set in px so the UI font-size preference can't change it. Its own constant:
 *  never the toolbar's default text size. */
export const HEADER_FOOTER_FONT_PX = 13.333;

/** The smallest top (bottom) margin that fits a running header (footer); for SP-152's margin UI. */
export const HEADER_FOOTER_MIN_BAND = 32;

export const HEADER_FOOTER_TOKENS = { page: '{page}', pages: '{pages}', name: '{name}' } as const;

export function emptyHeaderFooter(): HeaderFooter {
    return {
        header: { left: '', center: '', right: '' },
        footer: { left: '', center: '', right: '' },
        hideOnFirstPage: false,
    };
}

const TOKEN_PATTERN = /\{(page|pages|name)\}/g;

/** Resolves `{page}`, `{pages}` and `{name}` (exact, case-sensitive) in one pass, so a name that
 *  itself contains a token is never expanded again. Any other brace text prints as typed. */
export function resolveSlot(template: string, values: { page: number; pages: number; name: string }): string {
    return template.replace(TOKEN_PATTERN, (_, token: 'page' | 'pages' | 'name') =>
        token === 'name' ? (values.name ?? '') : String(values[token]),
    );
}

function rowEmpty(slots: HeaderFooterSlots): boolean {
    return SLOT_NAMES.every(slot => slots[slot] === '');
}

/** All six slots empty. */
export function isEmptyHeaderFooter(hf: HeaderFooter): boolean {
    return rowEmpty(hf.header) && rowEmpty(hf.footer);
}

/** Whether the pattern has a running header with any text. */
export function hasHeader(setup: PageSetup): boolean {
    return setup.headerFooter !== undefined && !rowEmpty(setup.headerFooter.header);
}

/** Whether the pattern has a running footer with any text. */
export function hasFooter(setup: PageSetup): boolean {
    return setup.headerFooter !== undefined && !rowEmpty(setup.headerFooter.footer);
}

/** Whether the band is drawn on the page at 0-based `pageIndex`. */
export function bandShownOn(setup: PageSetup, pageIndex: number): boolean {
    const hf = setup.headerFooter;
    if (hf === undefined) return false;
    return !(pageIndex === 0 && hf.hideOnFirstPage);
}

/** One line of slot text: each line break (CRLF, CR or LF) and each tab becomes one space;
 *  clipped to the slot limit. */
export function normalizeSlotText(raw: string): string {
    const clipped = raw.replace(/\r\n|[\r\n\t]/g, ' ').slice(0, HEADER_FOOTER_LIMITS.slot);
    // The clip must not split a surrogate pair: drop a trailing lone high surrogate.
    return /[\uD800-\uDBFF]$/.test(clipped) ? clipped.slice(0, -1) : clipped;
}
